import { RemovalBehavior, UpdateFrequency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Label, Select, SelectItem } from '@utima/ui';
import { Info } from 'lucide-react';
import { memo, type ReactNode } from 'react';

import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';

import { formatAutoUpdateCosts } from '../lib/autoUpdateCostCopy';
import { UPDATE_FREQUENCY_OPTIONS } from '../lib/updateFrequencyOptions';

type AutoUpdateFieldMode = 'single' | 'bulk';

export const BULK_KEEP_UNCHANGED_VALUE = '__keep_unchanged__';
export const DISCOVER_OFF_VALUE = '__discover_off__';

const THRESHOLD_OPTIONS = [
  { value: 0.01, label: '1%' },
  { value: 0.05, label: '5%' },
  { value: 0.1, label: '10%' },
  { value: 0.2, label: '20%' },
] as const;

const ACTIVE_UPDATE_FREQUENCIES = UPDATE_FREQUENCY_OPTIONS.filter(
  value => value !== UpdateFrequency.Never,
);

function FieldLabel({
  htmlFor,
  tooltip,
  children,
}: {
  htmlFor: string;
  tooltip: string;
  children: ReactNode;
}) {
  return (
    <div className='flex items-center gap-1.5'>
      <Label htmlFor={htmlFor}>{children}</Label>
      <SimpleTooltip title={tooltip}>
        <Info className='size-3.5 shrink-0 cursor-help text-muted-fg' />
      </SimpleTooltip>
    </div>
  );
}

export type AutoUpdateSettingsFormProps = {
  mode: AutoUpdateFieldMode;
  showUpdateFrequency?: boolean;
  showChangeThreshold?: boolean;
  showDiscoveryFrequency?: boolean;
  showWebsiteFields: boolean;
  showAutoIncludeNewPages?: boolean;
  showAutoIncludeRootScanHint?: boolean;
  updateFrequency: UpdateFrequency | null | undefined;
  discoveryFrequency?: UpdateFrequency | null | undefined;
  changeThreshold: number | null | undefined;
  autoIncludeNewPages?: boolean | null | undefined;
  removalBehavior?: RemovalBehavior | null | undefined;
  onUpdateFrequencyChange: (value: UpdateFrequency | null | undefined) => void;
  onDiscoveryFrequencyChange?: (
    value: UpdateFrequency | null | undefined,
  ) => void;
  onChangeThresholdChange: (value: number | null | undefined) => void;
  onAutoIncludeNewPagesChange?: (
    value: boolean | null | undefined,
  ) => void | undefined;
  onRemovalBehaviorChange?: (
    value: RemovalBehavior | null | undefined,
  ) => void | undefined;
};

export const AutoUpdateSettingsForm = memo(function AutoUpdateSettingsForm({
  mode,
  showUpdateFrequency = true,
  showChangeThreshold = false,
  showDiscoveryFrequency = false,
  showWebsiteFields,
  showAutoIncludeNewPages,
  showAutoIncludeRootScanHint = false,
  updateFrequency,
  discoveryFrequency,
  changeThreshold,
  autoIncludeNewPages,
  removalBehavior,
  onUpdateFrequencyChange,
  onDiscoveryFrequencyChange,
  onChangeThresholdChange,
  onAutoIncludeNewPagesChange,
  onRemovalBehaviorChange,
}: AutoUpdateSettingsFormProps) {
  const { t } = useLingui();
  const { organization } = useCurrentOrganization();
  const { check: checkCost, update: updateCost } = formatAutoUpdateCosts(
    organization?.currency,
  );
  const enumTranslations = useEnumTranslations();
  const isBulk = mode === 'bulk';

  const frequencyValue = isBulk
    ? updateFrequency === undefined
      ? BULK_KEEP_UNCHANGED_VALUE
      : (updateFrequency ?? 'inherit')
    : (updateFrequency ?? 'inherit');

  const discoveryValue = isBulk
    ? discoveryFrequency === undefined
      ? BULK_KEEP_UNCHANGED_VALUE
      : discoveryFrequency == null ||
          discoveryFrequency === UpdateFrequency.Never
        ? DISCOVER_OFF_VALUE
        : discoveryFrequency
    : discoveryFrequency == null || discoveryFrequency === UpdateFrequency.Never
      ? DISCOVER_OFF_VALUE
      : discoveryFrequency;

  const thresholdValue = isBulk
    ? changeThreshold === undefined
      ? BULK_KEEP_UNCHANGED_VALUE
      : changeThreshold == null
        ? 'inherit'
        : String(changeThreshold)
    : changeThreshold == null
      ? 'inherit'
      : String(changeThreshold);

  const autoIncludeValue = isBulk
    ? autoIncludeNewPages === undefined
      ? BULK_KEEP_UNCHANGED_VALUE
      : autoIncludeNewPages == null
        ? 'inherit'
        : autoIncludeNewPages
          ? 'enabled'
          : 'disabled'
    : autoIncludeNewPages == null
      ? 'inherit'
      : autoIncludeNewPages
        ? 'enabled'
        : 'disabled';

  const removalBehaviorValue = isBulk
    ? removalBehavior === undefined
      ? BULK_KEEP_UNCHANGED_VALUE
      : (removalBehavior ?? 'inherit')
    : (removalBehavior ?? 'inherit');

  const shouldShowAutoInclude =
    showWebsiteFields && (showAutoIncludeNewPages ?? true);

  return (
    <div className='space-y-4'>
      {showDiscoveryFrequency && (
        <div className='space-y-1.5'>
          <FieldLabel
            htmlFor='discoveryFrequency'
            tooltip={t`How often to scan this path for new and removed pages. About ${checkCost} per wholescan. It does not check the content of the pages themselves.`}
          >
            <Trans>Look for new pages</Trans>
          </FieldLabel>
          <Select
            id='discoveryFrequency'
            value={discoveryValue}
            onValueChange={value => {
              if (!onDiscoveryFrequencyChange) {
                return;
              }

              if (isBulk && value === BULK_KEEP_UNCHANGED_VALUE) {
                onDiscoveryFrequencyChange(undefined);

                return;
              }

              if (value === DISCOVER_OFF_VALUE) {
                onDiscoveryFrequencyChange(null);

                return;
              }

              onDiscoveryFrequencyChange(value as UpdateFrequency);
            }}
          >
            {isBulk && (
              <SelectItem value={BULK_KEEP_UNCHANGED_VALUE}>
                <Trans>Multiple values - keep current</Trans>
              </SelectItem>
            )}
            <SelectItem value={DISCOVER_OFF_VALUE}>
              <Trans>Off</Trans>
            </SelectItem>
            {ACTIVE_UPDATE_FREQUENCIES.map(value => (
              <SelectItem key={value} value={value}>
                {enumTranslations.updateFrequency[value]}
              </SelectItem>
            ))}
          </Select>
        </div>
      )}

      {shouldShowAutoInclude && (
        <div className='space-y-1.5'>
          <FieldLabel
            htmlFor='autoIncludeNewPages'
            tooltip={t`When looking for new pages, add them to the knowledge base automatically. Turn off on a subfolder (e.g. /case-studies) to keep those pages for review.`}
          >
            <Trans>Auto-include new pages</Trans>
          </FieldLabel>
          <Select
            id='autoIncludeNewPages'
            value={autoIncludeValue}
            onValueChange={value => {
              if (!onAutoIncludeNewPagesChange) {
                return;
              }

              if (isBulk && value === BULK_KEEP_UNCHANGED_VALUE) {
                onAutoIncludeNewPagesChange(undefined);

                return;
              }

              if (value === 'inherit') {
                onAutoIncludeNewPagesChange(null);

                return;
              }

              onAutoIncludeNewPagesChange(value === 'enabled');
            }}
          >
            {isBulk && (
              <SelectItem value={BULK_KEEP_UNCHANGED_VALUE}>
                <Trans>Multiple values - keep current</Trans>
              </SelectItem>
            )}
            <SelectItem value='inherit'>
              <Trans>Inherit</Trans>
            </SelectItem>
            <SelectItem value='enabled'>
              <Trans>Enabled</Trans>
            </SelectItem>
            <SelectItem value='disabled'>
              <Trans>Disabled</Trans>
            </SelectItem>
          </Select>
          {showAutoIncludeRootScanHint && (
            <p className='text-xs text-muted-fg'>
              <Trans>
                Look for new pages must be enabled on the website root, or this
                setting has no effect.
              </Trans>
            </p>
          )}
        </div>
      )}

      {showUpdateFrequency && (
        <div className='space-y-1.5'>
          <FieldLabel
            htmlFor='autoUpdateFrequency'
            tooltip={t`How often to re-check content of pages already in the knowledge base. About ${checkCost} per page check, up to ${updateCost} if the page actually changes.`}
          >
            <Trans>Keep included pages updated</Trans>
          </FieldLabel>
          <Select
            id='autoUpdateFrequency'
            value={frequencyValue}
            onValueChange={value => {
              if (isBulk && value === BULK_KEEP_UNCHANGED_VALUE) {
                onUpdateFrequencyChange(undefined);

                return;
              }

              if (value === 'inherit') {
                onUpdateFrequencyChange(null);

                return;
              }

              onUpdateFrequencyChange(value as UpdateFrequency);
            }}
          >
            {isBulk && (
              <SelectItem value={BULK_KEEP_UNCHANGED_VALUE}>
                <Trans>Multiple values - keep current</Trans>
              </SelectItem>
            )}
            <SelectItem value='inherit'>
              <Trans>Inherit from folder hierarchy</Trans>
            </SelectItem>
            {UPDATE_FREQUENCY_OPTIONS.map(value => (
              <SelectItem key={value} value={value}>
                {enumTranslations.updateFrequency[value]}
              </SelectItem>
            ))}
          </Select>
        </div>
      )}

      {showWebsiteFields && (
        <div className='space-y-1.5'>
          <FieldLabel
            htmlFor='removalBehavior'
            tooltip={t`What happens when a page disappears from the site. Flag marks it for review, Remove deletes it, Ignore leaves it unchanged.`}
          >
            <Trans>If source is removed</Trans>
          </FieldLabel>
          <Select
            id='removalBehavior'
            value={removalBehaviorValue}
            onValueChange={value => {
              if (!onRemovalBehaviorChange) {
                return;
              }

              if (isBulk && value === BULK_KEEP_UNCHANGED_VALUE) {
                onRemovalBehaviorChange(undefined);

                return;
              }

              if (value === 'inherit') {
                onRemovalBehaviorChange(null);

                return;
              }

              onRemovalBehaviorChange(value as RemovalBehavior);
            }}
          >
            {isBulk && (
              <SelectItem value={BULK_KEEP_UNCHANGED_VALUE}>
                <Trans>Multiple values - keep current</Trans>
              </SelectItem>
            )}
            <SelectItem value='inherit'>
              <Trans>Inherit</Trans>
            </SelectItem>
            {Object.values(RemovalBehavior).map(behavior => (
              <SelectItem key={behavior} value={behavior}>
                {enumTranslations.removalBehavior[behavior]}
              </SelectItem>
            ))}
          </Select>
        </div>
      )}

      {showChangeThreshold && (
        <div className='space-y-1.5'>
          <FieldLabel
            htmlFor='autoUpdateThreshold'
            tooltip={t`Minimum content change percentage to trigger re-embedding.`}
          >
            <Trans>Change threshold</Trans>
          </FieldLabel>
          <Select
            id='autoUpdateThreshold'
            value={thresholdValue}
            onValueChange={value => {
              if (isBulk && value === BULK_KEEP_UNCHANGED_VALUE) {
                onChangeThresholdChange(undefined);

                return;
              }

              if (value === 'inherit') {
                onChangeThresholdChange(null);

                return;
              }

              onChangeThresholdChange(Number(value));
            }}
          >
            {isBulk && (
              <SelectItem value={BULK_KEEP_UNCHANGED_VALUE}>
                <Trans>Multiple values - keep current</Trans>
              </SelectItem>
            )}
            <SelectItem value='inherit'>
              <Trans>Inherit from folder hierarchy</Trans>
            </SelectItem>
            {THRESHOLD_OPTIONS.map(option => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
});
